import React, { useState, useEffect, useCallback } from "react";
import {
  FaEye,
  FaDownload,
  FaFileCsv,
  FaFileExcel,
  FaFileCode,
  FaPlus,
  FaEdit,
  FaFilter,
} from "react-icons/fa";
import "./TaskBoard.css";
import { toast } from "react-toastify";
import TaskDetailsModal from "./TaskDetailsModal";
import Select from "react-select";
import getApiUrl from "../utils/api";
import { usePostHog } from "posthog-js/react";
import AddTaskModal from './AddTaskModal';
import Pagination from './Pagination';


const statusCards = [
  { label: "All", color: "#7b6ef6" },
  { label: "Pending", color: "#a259e6" },
  { label: "Planned", color: "#4fc3f7" },
  { label: "WIP", color: "#ff9800" },
  { label: "Completed", color: "#4caf50" },
  { label: "Revalidate", color: "#f48fb1" },
  { label: "Delegated", color: "#ffb300" },
  { label: "Reopen", color: "#90a4ae" },
];

const MAX_STATUS_ATTACHMENTS = 5;

const formatFileSize = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes)) || Number(bytes) <= 0) {
    return "";
  }
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(bytes);
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const rounded = value >= 100 || value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
};

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value === "number") return new Date(value);

  const raw = String(value || "").trim();
  if (!raw) return null;

  // Handle DD/MM/YYYY or DD-MM-YYYY patterns
  const dmyMatch = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dmyMatch) {
    const day = parseInt(dmyMatch[1], 10);
    const month = parseInt(dmyMatch[2], 10) - 1;
    const year = parseInt(dmyMatch[3], 10);

    const timePart = raw.match(/[ \s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/);
    const date = timePart
      ? new Date(year, month, day, parseInt(timePart[1], 10), parseInt(timePart[2] || 0, 10), parseInt(timePart[3] || 0, 10))
      : new Date(year, month, day);

    if (!timePart) {
      date.setHours(23, 59, 59, 999);
    }
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw.replace(" ", "T"));
  if (!isNaN(date.getTime())) {
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      date.setHours(23, 59, 59, 999);
    }
    return date;
  }
  return null;
}

const resolveTaskTargetDate = (task) =>
  task?.target_date ||
  task?.targetDate ||
  task?.target_end_date ||
  task?.targetEndDate ||
  task?.due_date ||
  task?.dueDate ||
  task?.end_date ||
  task?.endDate ||
  null;

const resolveTaskStartDate = (task) =>
  task?.start_date ||
  task?.startDate ||
  task?.created_at ||
  task?.createdAt ||
  task?.due_date ||
  task?.dueDate ||
  null;

function resolveTaskCompletionDate(task) {
  if (!task) return null;

  const normalizedStatus = (task?.status || task?.statusForCurrentUser || task?.originalStatus || "")
    .toString()
    .trim()
    .toLowerCase();


  const candidates = [
    task?.completed_at,
    task?.completedAt,
    task?.completion_at,
    task?.completionAt,
    task?.completed_on,
    task?.completedOn,
    task?.status_completed_at,
    task?.statusCompletedAt,
    task?.last_completed_at,
    task?.lastCompletedAt,
    task?.overdueCompletionAt,
    task?.overdue_completion_at,
  ];

  for (const value of candidates) {
    const parsed = toDate(value);
    if (parsed) {
      return parsed;
    }
  }

  if (typeof task?.overdueCompletionTimestamp === "number" && !Number.isNaN(task.overdueCompletionTimestamp)) {
    const timestampDate = toDate(task.overdueCompletionTimestamp);
    if (timestampDate) {
      return timestampDate;
    }
  }

  const isCompleted = normalizedStatus === "completed" || normalizedStatus === "complete" || normalizedStatus === "done";

  if (isCompleted) {
    const fallback = [
      task?.status_updated_at,
      task?.statusUpdatedAt,
      task?.completed_timestamp,
      task?.completedTimestamp,
      task?.updated_at,
      task?.updatedAt,
      task?.completed_at,
      task?.completedAt,
      task?.status_completed_at,
      task?.statusCompletedAt,
    ];

    for (const value of fallback) {
      const parsed = toDate(value);
      if (parsed) {
        return parsed;
      }
    }
  }

  return null;
}

function resolveTaskCompletionTimestamp(task) {
  if (!task) return null;

  const status = (task?.status || task?.statusForCurrentUser || "").toString().trim().toLowerCase();
  const isCompleted = status === "completed" || status === "complete" || status === "done";

  if (typeof task.overdueCompletionTimestamp === "number" && !Number.isNaN(task.overdueCompletionTimestamp)) {
    return task.overdueCompletionTimestamp;
  }

  // If it's a group entry, use the aggregated ageingCompletionTimestamp ONLY if status is fully completed
  if (typeof task.ageingCompletionTimestamp === "number" && !Number.isNaN(task.ageingCompletionTimestamp)) {
    if (isCompleted) {
      return task.ageingCompletionTimestamp;
    }
    // If status is "Mixed" or something else, we don't cap aging yet
    return null;
  }

  const parsed = toDate(task?.overdueCompletionTimestamp);
  if (parsed) {
    return parsed.getTime();
  }

  const completionDate = resolveTaskCompletionDate(task);
  return completionDate ? completionDate.getTime() : null;
}

function calculateDelayMs(targetDateValue, cutoffDateValue = null) {
  const anchor = toDate(targetDateValue);
  if (!anchor) return null;

  let comparisonTimestamp;
  if (cutoffDateValue !== null && cutoffDateValue !== undefined) {
    const cutoffDate = toDate(cutoffDateValue);
    if (!cutoffDate) return null;
    comparisonTimestamp = cutoffDate.getTime();
  } else {
    comparisonTimestamp = Date.now();
  }

  const diff = comparisonTimestamp - anchor.getTime();
  return diff <= 0 ? 0 : diff;
}

function calculateOverdueMinutes(targetDateValue, cutoffDateValue = null) {
  const delayMs = calculateDelayMs(targetDateValue, cutoffDateValue);
  if (delayMs === null) return null;
  return Math.floor(delayMs / 60000);
}

function calculateOverdueDisplay(targetDateValue, cutoffDateValue = null) {
  const delayMs = calculateDelayMs(targetDateValue, cutoffDateValue);
  if (delayMs === null) return "";
  if (delayMs <= 0) return "0d";

  const seconds = Math.floor(delayMs / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const remM = minutes % 60;
    return remM > 0 ? `${hours}h ${remM}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    const remH = hours % 24;
    return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    const remD = days % 30;
    return remD > 0 ? `${months}mo ${remD}d` : `${months}mo`;
  }

  const years = Math.floor(months / 12);
  const remMo = months % 12;
  return remMo > 0 ? `${years}y ${remMo}mo` : `${years}y`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const statusPriorityOrder = [
  "pending",
  "planned",
  "wip",
  "hold",
  "revalidate",
  "delegated",
  "reopen",
  "other",
];

const badgeClassLookup = {
  pending: "Pending",
  planned: "Planned",
  wip: "WIP",
  hold: "Pending",
  revalidate: "Revalidate",
  delegated: "Delegated",
  reopen: "Reopen",
  completed: "Completed",
};

function statusPriorityIndex(status) {
  const idx = statusPriorityOrder.indexOf(status);
  return idx === -1 ? statusPriorityOrder.length : idx;
}

function resolveAggregatedDisplayStatus(task, resolveStatusLabel) {
  const fallbackLabel = resolveStatusLabel(task?.status);
  const fallbackNormalized = fallbackLabel.toLowerCase();

  if (
    !task?.statusBreakdown ||
    typeof task.statusBreakdown !== "object" ||
    Object.keys(task.statusBreakdown).length === 0
  ) {
    return {
      label: fallbackLabel,
      normalized: fallbackNormalized,
    };
  }

  const entries = Object.entries(task.statusBreakdown)
    .map(([status, value]) => {
      const label = resolveStatusLabel(status);
      const normalized = (status || "")
        .toString()
        .trim()
        .toLowerCase();
      return {
        label,
        normalized,
        value: Number(value) || 0,
      };
    })
    .filter((entry) => entry.value > 0);

  if (entries.length === 0) {
    return {
      label: fallbackLabel,
      normalized: fallbackNormalized,
    };
  }

  const totalAssignees = entries.reduce((sum, entry) => sum + entry.value, 0);
  const distinctStatuses = new Set(entries.map((entry) => entry.normalized));

  if (distinctStatuses.size === 1) {
    const [onlyEntry] = entries;
    if (onlyEntry.normalized === "completed") {
      return { label: "Completed", normalized: "completed" };
    }
    return {
      label: onlyEntry.label,
      normalized: onlyEntry.normalized || fallbackNormalized,
    };
  }

  const outstanding = entries.filter(
    (entry) => entry.normalized !== "completed"
  );

  if (outstanding.length === 0) {
    return { label: "Completed", normalized: "completed" };
  }

  outstanding.sort(
    (a, b) => statusPriorityIndex(a.normalized) - statusPriorityIndex(b.normalized)
  );

  const outstandingTotal = outstanding.reduce(
    (sum, entry) => sum + entry.value,
    0
  );

  const primary = outstanding[0];
  const label =
    outstanding.length > 1
      ? `${primary.label} (${outstandingTotal}/${totalAssignees})`
      : primary.label;

  return {
    label,
    normalized: primary.normalized || fallbackNormalized,
  };
}

function formatStatusBreakdownTooltip(task, resolveStatusLabel) {
  if (
    !task?.statusBreakdown ||
    typeof task.statusBreakdown !== "object" ||
    Object.keys(task.statusBreakdown).length === 0
  ) {
    return "";
  }

  const parts = Object.entries(task.statusBreakdown)
    .map(([status, count]) => {
      const label = resolveStatusLabel(status);
      return `${label}: ${count}`;
    })
    .filter(Boolean);

  return parts.join(", ");
}

const TaskBoard = () => {
  const [showAddTask, setShowAddTask] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [tasks, setTasks] = useState([]);
  const [pendingFocusId, setPendingFocusId] = useState(null);
  const [highlightTaskId, setHighlightTaskId] = useState(null);
  const [, setNowTick] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionTask, setActionTask] = useState(null);
  const [userId, setUserId] = useState(null);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [selectedStatusOption, setSelectedStatusOption] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [statusRemark, setStatusRemark] = useState("");
  const [statusAttachments, setStatusAttachments] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState(""); // Search term actually applied
  const [statusCounts, setStatusCounts] = useState({ All: 0 }); // Server-side counts

  // Debounce search query - REMOVED for manual apply
  /*
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  */

  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkCompleteLoading, setBulkCompleteLoading] = useState(false);
  const [showBulkCompleteModal, setShowBulkCompleteModal] = useState(false);
  const [bulkCompleteTasks, setBulkCompleteTasks] = useState([]);
  const [delegateOptions, setDelegateOptions] = useState([]);
  const [delegateOptionsLoading, setDelegateOptionsLoading] = useState(false);
  const [showDelegationComposer, setShowDelegationComposer] = useState(false);
  const [delegationDraft, setDelegationDraft] = useState({
    employee: null,
    taskName: "",
    description: "",
  });
  const [delegationEntries, setDelegationEntries] = useState([]);
  const [delegationDraftError, setDelegationDraftError] = useState("");
  const posthog = usePostHog();
  const [isTaskReloading, setIsTaskReloading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(50);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(1);


  const statusLabelLookup = React.useMemo(() => {
    const map = new Map();
    statusCards.forEach((card) => map.set(card.label.toLowerCase(), card.label));
    return map;
  }, []);

  const resolveStatusLabel = React.useCallback(
    (value) => {
      const fallback = (value || "Pending").toString().trim();
      if (!fallback) return "Pending";
      const match = statusLabelLookup.get(fallback.toLowerCase());
      return match || fallback;
    },
    [statusLabelLookup]
  );

  const resolveOverdueMinutes = React.useCallback(
    (task) => {
      if (!task) return null;
      const { normalized } = resolveAggregatedDisplayStatus(
        task,
        resolveStatusLabel
      );
      const completionTimestamp =
        normalized === "completed"
          ? resolveTaskCompletionTimestamp(task)
          : null;
      return calculateOverdueMinutes(
        resolveTaskTargetDate(task),
        completionTimestamp
      );
    },
    [resolveStatusLabel]
  );

  const resolveOverdueDisplayString = React.useCallback(
    (task) => {
      if (!task) return "";
      const { normalized } = resolveAggregatedDisplayStatus(
        task,
        resolveStatusLabel
      );
      const completionTimestamp =
        normalized === "completed"
          ? resolveTaskCompletionTimestamp(task)
          : null;
      return calculateOverdueDisplay(
        resolveTaskTargetDate(task),
        completionTimestamp
      );
    },
    [resolveStatusLabel]
  );

  // Status counts are now fetched from server
  // const statusCounts = React.useMemo(...) - REMOVED

  const fetchTasks = useCallback(async () => {
    if (!userId) return;
    setIsTaskReloading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', pageLimit);

      // Pass selected status to backend (except 'All')
      if (selectedStatus !== 'All') {
        params.append('status', selectedStatus);
      }

      // Add search parameter for task name or email (ONLY if applied)
      if (appliedSearch && appliedSearch.trim()) {
        params.append('search', appliedSearch.trim());
      }

      const url = `api/task/myTask/${userId}?${params.toString()}`;
      const res = await fetch(getApiUrl(url), {
        credentials: "include",
      });
      const data = await res.json();

      // Handle both old and new response formats
      let tasksArr = [];
      let pagination = null;
      let counts = { All: 0 };

      if (Array.isArray(data)) {
        tasksArr = data;
      } else if (data && Array.isArray(data.tasks)) {
        tasksArr = data.tasks;
        pagination = data.pagination;
        counts = data.statusCounts || { All: 0 };
      }

      // Update pagination state
      if (pagination) {
        setTotalTasks(pagination.total || 0);
        setTotalPages(pagination.totalPages || 1);
      }

      // Update status counts from server
      setStatusCounts(counts);

      // Sort by resolved target date ascending (soonest first)
      tasksArr.sort((a, b) => {
        const dateA = toDate(resolveTaskTargetDate(a));
        const dateB = toDate(resolveTaskTargetDate(b));
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA - dateB;
      });
      setTasks(tasksArr);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setTasks([]);
    } finally {
      setIsTaskReloading(false);
    }
  }, [userId, currentPage, pageLimit, appliedSearch, selectedStatus]);

  useEffect(() => {
    fetch(getApiUrl("api/auth/me"), {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.user) {
          setUserId(data.user.userId || data.user.id || data.user.sub);
          setCurrentUserInfo(data.user);
        }
      });
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    let cancelled = false;

    const fetchDelegationTargets = async () => {
      setDelegateOptionsLoading(true);
      try {
        const response = await fetch(getApiUrl("api/auth/employees"), {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(`Failed to load employees (${response.status})`);
        }
        const data = await response.json();
        if (cancelled) return;

        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.employees)
            ? data.employees
            : Array.isArray(data?.data)
              ? data.data
              : [];

        const options = items
          .map((item) => {
            if (!item) return null;
            const idCandidate =
              item.userId || item.user_id || item.id || item.EmployeeID;
            if (idCandidate === undefined || idCandidate === null) {
              return null;
            }

            const firstName =
              item.first_name || item.FirstName || item.firstName || "";
            const lastName =
              item.last_name || item.LastName || item.lastName || "";
            const rawEmail = item.email || item.Email || item.email_id || "";
            const email = typeof rawEmail === "string" ? rawEmail.trim() : "";

            const namePart = `${firstName} ${lastName}`
              .replace(/\s+/g, " ")
              .trim();
            const label = namePart || email || String(idCandidate);

            const searchText = [label, email, String(idCandidate)]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();

            return {
              value: String(idCandidate),
              label,
              meta: {
                email,
              },
              searchText,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));

        setDelegateOptions(options);
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading employees for delegation:", error);
          setDelegateOptions([]);
        }
      } finally {
        if (!cancelled) {
          setDelegateOptionsLoading(false);
        }
      }
    };

    fetchDelegationTargets();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedStatusOption || selectedStatusOption.value !== "Delegated") {
      setShowDelegationComposer(false);
      setDelegationEntries([]);
      setDelegationDraft({ employee: null, taskName: "", description: "" });
      setDelegationDraftError("");
    }
  }, [selectedStatusOption]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    const stringUserId = String(userId);
    const firstName =
      currentUserInfo?.FirstName || currentUserInfo?.first_name || "";
    const lastName =
      currentUserInfo?.LastName || currentUserInfo?.last_name || "";
    const email = currentUserInfo?.email || currentUserInfo?.Email || "";
    const displayName = `${firstName} ${lastName}`
      .replace(/\s+/g, " ")
      .trim();
    const label = displayName || email || stringUserId;
    const searchText = [label, email, stringUserId]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const existing = delegateOptions.find(
      (option) => String(option.value) === stringUserId
    );

    if (existing) {
      const needsUpdate =
        (label && existing.label !== label) ||
        (email && existing.meta?.email !== email) ||
        (!email && existing.meta?.email);
      if (!needsUpdate) {
        return;
      }
      const updated = delegateOptions
        .map((option) => {
          if (String(option.value) !== stringUserId) {
            return option;
          }
          return {
            ...option,
            label,
            meta: { ...(option.meta || {}), email },
            searchText,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
      setDelegateOptions(updated);
      return;
    }

    const selfOption = {
      value: stringUserId,
      label,
      meta: { email },
      searchText,
    };

    const next = [...delegateOptions, selfOption].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    setDelegateOptions(next);
  }, [userId, currentUserInfo, delegateOptions]);

  const formatDelegateOption = useCallback((option, { context }) => {
    if (!option) {
      return null;
    }
    const email = option.meta?.email || "";
    const name = option.label || "";
    const isValueContext = context === "value";

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "100%",
          lineHeight: 1.25,
        }}
      >
        <span
          style={{
            fontSize: isValueContext ? "0.88rem" : "0.9rem",
            fontWeight: 600,
            color: "#1f2937",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={name}
        >
          {name}
        </span>
        {email ? (
          <span
            style={{
              fontSize: isValueContext ? "0.74rem" : "0.78rem",
              color: "#5f6b7d",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={email}
          >
            {email}
          </span>
        ) : null}
      </div>
    );
  }, []);

  const delegateFilterOption = useCallback((candidate, rawInput) => {
    if (!rawInput) {
      return true;
    }
    const input = rawInput.trim().toLowerCase();
    if (!input) {
      return true;
    }
    const haystack = candidate?.data?.searchText || "";
    return haystack.includes(input);
  }, []);

  useEffect(() => {
    const storedFocusId = sessionStorage.getItem("mytask:focusTaskId");
    if (storedFocusId) {
      setPendingFocusId(storedFocusId);
      sessionStorage.removeItem("mytask:focusTaskId");
    }
  }, []);

  useEffect(() => {
    if (!pendingFocusId || !Array.isArray(tasks) || tasks.length === 0) {
      return;
    }

    const targetId = String(pendingFocusId);
    const match = tasks.find((task) => String(task?.id) === targetId);

    if (match) {
      setHighlightTaskId(String(match.id));
      setPendingFocusId(null);
    }
  }, [pendingFocusId, tasks]);

  // Re-render every second to keep ageing live (seconds -> minutes etc.)
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => (t + 1) % 1_000_000), 1000);
    return () => clearInterval(id);
  }, []);

  // Enhanced filtering with search
  // Client side filtering removed - relying on server side
  const filteredTasks = tasks;

  useEffect(() => {
    if (!highlightTaskId) return;
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const attemptFocus = () => {
      const selector = `[data-task-row="${highlightTaskId}"]`;
      const row = document.querySelector(selector);
      if (row && typeof row.scrollIntoView === "function") {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        return true;
      }
      return false;
    };

    const immediate = attemptFocus();
    let retryTimer = null;

    if (!immediate) {
      retryTimer = window.setTimeout(attemptFocus, 180);
    }

    const clearTimer = window.setTimeout(() => {
      setHighlightTaskId(null);
    }, 3500);

    return () => {
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      window.clearTimeout(clearTimer);
    };
  }, [highlightTaskId, filteredTasks]);

  // Handle action button click
  const handleActionClick = (task) => {
    setActionTask(task);
    setSelectedStatusOption({ value: task.status, label: task.status });
    setUpdateLoading(false);
    setUpdateError("");
    setStatusRemark("");
    setStatusAttachments([]);
    setShowDelegationComposer(false);
    setDelegationDraft({ employee: null, taskName: "", description: "" });
    setDelegationEntries([]);
    setDelegationDraftError("");
    setShowActionModal(true);
  };

  const handleCloseActionModal = () => {
    setShowActionModal(false);
    setActionTask(null);
    setSelectedStatusOption(null);
    setStatusRemark("");
    setUpdateError("");
    setStatusAttachments([]);
    setShowDelegationComposer(false);
    setDelegationDraft({ employee: null, taskName: "", description: "" });
    setDelegationEntries([]);
    setDelegationDraftError("");
  };

  const handleStatusAttachmentInput = (event) => {
    const incomingFiles = Array.from(event.target.files || []);
    if (incomingFiles.length === 0) return;

    let limitReached = false;
    setStatusAttachments((prev) => {
      const availableSlots = Math.max(0, MAX_STATUS_ATTACHMENTS - prev.length);
      if (availableSlots <= 0) {
        limitReached = true;
        return prev;
      }
      const filesToAdd = incomingFiles.slice(0, availableSlots);
      if (filesToAdd.length < incomingFiles.length) {
        limitReached = true;
      }
      return filesToAdd.length > 0 ? [...prev, ...filesToAdd] : prev;
    });

    if (limitReached) {
      setUpdateError(
        `You can attach up to ${MAX_STATUS_ATTACHMENTS} files per status update.`
      );
    } else {
      setUpdateError("");
    }

    // Allow selecting the same file again after clearing
    if (event.target) {
      event.target.value = "";
    }
  };

  const handleToggleDelegationComposer = () => {
    setShowDelegationComposer((prev) => {
      const next = !prev;
      if (!next) {
        setDelegationDraft({ employee: null, taskName: "", description: "" });
        setDelegationDraftError("");
      } else {
        setDelegationDraftError("");
      }
      return next;
    });
  };

  const handleDelegationDraftEmployeeChange = (option) => {
    setDelegationDraft((prev) => ({ ...prev, employee: option }));
    setDelegationDraftError("");
  };

  const handleDelegationDraftFieldChange = (field) => (event) => {
    const value = event?.target?.value || "";
    setDelegationDraft((prev) => ({ ...prev, [field]: value }));
    setDelegationDraftError("");
  };

  const handleAddDelegationEntry = () => {
    const employeeOption = delegationDraft.employee;
    const taskName = (delegationDraft.taskName || "").trim();
    const description = (delegationDraft.description || "").trim();

    if (!employeeOption) {
      setDelegationDraftError("Please select an employee to delegate to.");
      return;
    }
    if (!taskName) {
      setDelegationDraftError("Please enter a task name for this delegation.");
      return;
    }

    const newEntry = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      employee: employeeOption,
      taskName,
      description,
    };

    setDelegationEntries((prev) => [...prev, newEntry]);
    setDelegationDraft({ employee: null, taskName: "", description: "" });
    setDelegationDraftError("");
  };

  const handleRemoveDelegationEntry = (entryId) => {
    setDelegationEntries((prev) => prev.filter((entry) => entry.id !== entryId));
  };

  const handleRemoveStatusAttachment = (index) => {
    setStatusAttachments((prev) => prev.filter((_, idx) => idx !== index));
    setUpdateError("");
  };

  // Handle edit task
  // Export functions
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error("No data to export!");
      return;
    }

    // Define CSV headers
    const headers = [
      "S. No.",
      "Task Name",
      "Description",
      "Status",
      "Priority",
      "Target Date",
      "Created Date",
      "Over Due",
      "Given By",
      "Given By Email",
      "Assigned To",
      "Project",
      "Department",
    ];

    // Prepare CSV data
    const csvData = data.map((task, index) => [
      index + 1,
      task.title || task.name || "",
      task.description || "",
      task.status || "",
      task.priority || "",
      task.due_date ? new Date(task.due_date).toLocaleDateString() : "",
      task.created_at ? new Date(task.created_at).toLocaleDateString() : "",
      resolveOverdueDisplayString(task),
      `${task.GivenByFirstName || ""} ${task.GivenByLastName || ""}`.trim() ||
      `${task.FirstName || ""} ${task.LastName || ""}`.trim() ||
      task.givenBy ||
      "",
      task.GivenByEmail || task.email || task.givenByEmail || "",
      task.assigned_to || "",
      task.project || "",
      task.department || "",
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = async (data, filename) => {
    if (!data || data.length === 0) {
      toast.error("No data to export!");
      return;
    }

    try {
      // Dynamic import of xlsx library
      const XLSX = await import("xlsx");

      // Prepare data for Excel
      const excelData = data.map((task, index) => ({
        "S. No.": index + 1,
        "Task Name": task.title || task.name || "",
        Description: task.description || "",
        Status: task.status || "",
        Priority: task.priority || "",
        "Target Date": task.due_date
          ? new Date(task.due_date).toLocaleDateString()
          : "",
        "Created Date": task.created_at
          ? new Date(task.created_at).toLocaleDateString()
          : "",
        "Over Due": resolveOverdueDisplayString(task),
        "Given By":
          `${task.GivenByFirstName || ""} ${task.GivenByLastName || ""
            }`.trim() ||
          `${task.FirstName || ""} ${task.LastName || ""}`.trim() ||
          task.givenBy ||
          "",
        "Given By Email":
          task.GivenByEmail || task.email || task.givenByEmail || "",
        "Assigned To": task.assigned_to || "",
        Project: task.project || "",
        Department: task.department || "",
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Auto-size columns
      const columnWidths = Object.keys(excelData[0]).map((key) => ({
        wch: Math.max(
          key.length,
          ...excelData.map((row) => String(row[key]).length)
        ),
      }));
      worksheet["!cols"] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tasks");

      // Generate and download file
      XLSX.writeFile(workbook, `${filename}.xlsx`);
    } catch (error) {
      console.error("Excel export failed:", error);
      toast.error("Excel export failed. Please try CSV export instead.");
    }
  };

  const exportToJSON = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error("No data to export!");
      return;
    }

    // Prepare JSON data with better formatting
    const jsonData = {
      exportInfo: {
        exportedAt: new Date().toISOString(),
        totalTasks: data.length,
        status: selectedStatus,
        searchQuery: searchQuery || null,
        filters: {
          status: selectedStatus,
          search: searchQuery || null,
        },
      },
      tasks: data.map((task, index) => ({
        serialNumber: index + 1,
        taskName: task.title || task.name || "",
        description: task.description || "",
        status: task.status || "",
        priority: task.priority || "",
        targetDate: task.due_date
          ? new Date(task.due_date).toLocaleDateString()
          : "",
        createdDate: task.created_at
          ? new Date(task.created_at).toLocaleDateString()
          : "",
        overdue: resolveOverdueDisplayString(task),
        givenBy:
          `${task.GivenByFirstName || ""} ${task.GivenByLastName || ""
            }`.trim() ||
          `${task.FirstName || ""} ${task.LastName || ""}`.trim() ||
          task.givenBy ||
          "",
        givenByEmail:
          task.GivenByEmail || task.email || task.givenByEmail || "",
        assignedTo: task.assigned_to || "",
        project: task.project || "",
        department: task.department || "",
        rawData: task, // Include original task data for reference
      })),
    };

    // Create and download file
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.json`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTasks([]);
      setSelectAll(false);
    } else {
      setSelectedTasks(filteredTasks.map((task) => task.id));
      setSelectAll(true);
    }
  };

  // Handle individual task selection
  const handleTaskSelect = (taskId) => {
    if (selectedTasks.includes(taskId)) {
      setSelectedTasks(selectedTasks.filter((id) => id !== taskId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedTasks, taskId];
      setSelectedTasks(newSelected);
      if (newSelected.length === filteredTasks.length) {
        setSelectAll(true);
      }
    }
  };

  // Get data to export (selected tasks or all filtered tasks)
  const getExportData = () => {
    if (selectedTasks.length > 0) {
      return filteredTasks.filter((task) => selectedTasks.includes(task.id));
    }
    return filteredTasks;
  };

  // Handle bulk complete
  const handleBulkComplete = () => {
    if (selectedTasks.length === 0) {
      toast.error("Please select tasks to complete!");
      return;
    }

    const tasksToComplete = filteredTasks.filter((task) =>
      selectedTasks.includes(task.id)
    );
    const incompleteTasks = tasksToComplete.filter(
      (task) => task.status !== "Completed"
    );

    if (incompleteTasks.length === 0) {
      toast.error("All selected tasks are already completed!");
      return;
    }

    setBulkCompleteTasks(incompleteTasks);
    setShowBulkCompleteModal(true);
  };

  // Execute bulk complete
  const executeBulkComplete = async () => {
    setBulkCompleteLoading(true);
    // Clear selection
    setSelectedTasks([]);
    setSelectAll(false);

    try {
      // Update each task status to completed
      const updatePromises = bulkCompleteTasks.map((task) =>
        fetch(getApiUrl(`api/task/${task.id}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...task,
            status: "Completed",
            currentUserId: userId,
          }),
          credentials: "include",
        })
      );

      const results = await Promise.all(updatePromises);
      const failed = [];
      for (const res of results) {
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          failed.push(data.error || data.message || "Unknown error");
        }
      }

      if (failed.length > 0) {
        throw new Error(failed[0]);
      }

      toast.success(
        `${bulkCompleteTasks.length} task(s) marked as completed successfully!`
      );

      // Update UI only after successful response
      setTasks(prev =>
        prev.map((task) =>
          selectedTasks.includes(task.id) && task.status !== "Completed"
            ? { ...task, status: "Completed" }
            : task
        )
      );
      setShowBulkCompleteModal(false);

    } catch (error) {
      console.error("Bulk complete failed:", error);
      toast.error("Failed to complete tasks. Please try again.");
    } finally {
      setBulkCompleteLoading(false);
    }
  };

  const handleExport = (format) => {
    setExportLoading(true);

    // Get data to export based on selection or filters
    const dataToExport = getExportData();

    if (dataToExport.length === 0) {
      toast.error("No data to export for the current selection!");
      setExportLoading(false);
      return;
    }

    // Create filename with selection info
    let filename = `MyTasks_${selectedTasks.length > 0 ? "Selected" : selectedStatus
      }`;
    if (searchQuery) {
      filename += `_Search_${searchQuery.replace(/[^a-zA-Z0-9]/g, "_")}`;
    }
    if (selectedTasks.length > 0) {
      filename += `_${selectedTasks.length}Tasks`;
    }
    filename += `_${new Date().toISOString().split("T")[0]}`;

    try {
      if (format === "csv") {
        exportToCSV(dataToExport, filename);
      } else if (format === "excel") {
        exportToExcel(dataToExport, filename);
      } else if (format === "json") {
        exportToJSON(dataToExport, filename);
      }

      toast.success(`Data exported successfully as ${format.toUpperCase()}!`);
      setShowExportModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="taskboard">

      <AddTaskModal
        open={showAddTask}
        onClose={() => setShowAddTask(false)}
        onTaskCreated={() => {
          fetchTasks();
        }}
      />

      <TaskDetailsModal
        open={showDetails}
        onClose={() => setShowDetails(false)}
        task={selectedTask}
        showGivenBy={true}
      />


      {/* Bulk Complete Confirmation Modal */}
      {showBulkCompleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              animation: "fadeInModal 0.3s ease-out",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                color: "#22223b",
                fontSize: "1.2rem",
                fontWeight: 700,
              }}
            >
              Confirm Bulk Complete
            </h3>
            <p
              style={{
                margin: "0 0 20px 0",
                color: "#666",
                fontSize: "0.95rem",
                lineHeight: 1.5,
              }}
            >
              Are you sure you want to mark{" "}
              <strong>{bulkCompleteTasks.length} task(s)</strong> as completed?
            </p>

            <div
              style={{
                background: "#f8f9fa",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "20px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#666",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Tasks to be completed:
              </div>
              {bulkCompleteTasks.slice(0, 5).map((task, index) => (
                <div
                  key={task.id}
                  style={{
                    fontSize: "0.8rem",
                    color: "#333",
                    padding: "4px 0",
                    borderBottom:
                      index < Math.min(5, bulkCompleteTasks.length - 1)
                        ? "1px solid #eee"
                        : "none",
                  }}
                >
                  • {task.title || task.name || "Untitled Task"}
                </div>
              ))}
              {bulkCompleteTasks.length > 5 && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    color: "#666",
                    fontStyle: "italic",
                    padding: "4px 0",
                  }}
                >
                  ... and {bulkCompleteTasks.length - 5} more task(s)
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowBulkCompleteModal(false)}
                style={{
                  padding: "10px 20px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  background: "#fff",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#f5f5f5";
                  e.target.style.borderColor = "#ccc";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "#fff";
                  e.target.style.borderColor = "#ddd";
                }}
              >
                Cancel
              </button>
              <button
                onClick={executeBulkComplete}
                disabled={bulkCompleteLoading}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "6px",
                  background: bulkCompleteLoading ? "#ccc" : "#4caf50",
                  color: "#fff",
                  cursor: bulkCompleteLoading ? "not-allowed" : "pointer",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseOver={(e) => {
                  if (!bulkCompleteLoading) {
                    e.target.style.background = "#45a049";
                  }
                }}
                onMouseOut={(e) => {
                  if (!bulkCompleteLoading) {
                    e.target.style.background = "#4caf50";
                  }
                }}
              >
                {bulkCompleteLoading ? (
                  <>
                    <span
                      className="spinner"
                      style={{
                        width: 16,
                        height: 16,
                        border: "2px solid #fff",
                        borderTop: "2px solid #4caf50",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin 0.7s linear infinite",
                      }}
                    ></span>
                    Completing...
                  </>
                ) : (
                  "Complete Tasks"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div
          className="export-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowExportModal(false)}
        >
          <div
            className="export-modal"
            style={{
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 8px 32px rgba(60,60,120,0.18)",
              minWidth: 400,
              maxWidth: 500,
              width: "90%",
              padding: "32px 28px 28px 28px",
              position: "relative",
              animation: "fadeInModal 0.3s cubic-bezier(.4,2,.6,1)",
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="export-modal-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <h3
                style={{
                  fontSize: "1.35rem",
                  fontWeight: 700,
                  color: "#22223b",
                  margin: 0,
                  letterSpacing: "-0.5px",
                }}
              >
                Export Task Data
              </h3>
              <button
                className="export-modal-close"
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 22,
                  color: "#888",
                  cursor: "pointer",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#f2f2f2")
                }
                onMouseOut={(e) => (e.currentTarget.style.background = "none")}
                onClick={() => setShowExportModal(false)}
              >
                ×
              </button>
            </div>

            <div
              className="export-modal-content"
              style={{
                marginBottom: 20,
                color: "#444",
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              <p
                style={{
                  margin: "0 0 18px 0",
                  color: "#6c6c80",
                  fontWeight: 500,
                }}
              >
                Export{" "}
                {selectedTasks.length > 0
                  ? `${selectedTasks.length} selected`
                  : selectedStatus === "All"
                    ? "all"
                    : selectedStatus.toLowerCase()}{" "}
                tasks ({getExportData().length} tasks)
                {searchQuery && (
                  <span style={{ color: "#7b6ef6", fontWeight: 600 }}>
                    {" "}
                    • Filtered by search: "{searchQuery}"
                  </span>
                )}
                {selectedTasks.length > 0 && (
                  <span style={{ color: "#4caf50", fontWeight: 600 }}>
                    {" "}
                    • {selectedTasks.length} tasks selected
                  </span>
                )}
              </p>

              <div
                className="export-options"
                style={{ display: "flex", flexDirection: "column", gap: 15 }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    style={{
                      padding: "16px 20px",
                      borderRadius: 12,
                      background: "#4caf50",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      border: "none",
                      cursor: exportLoading ? "not-allowed" : "pointer",
                      opacity: exportLoading ? 0.6 : 1,
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                      boxShadow: "0 4px 16px rgba(76, 175, 80, 0.3)",
                      minWidth: "140px",
                    }}
                    disabled={exportLoading}
                    onClick={() => handleExport("csv")}
                  >
                    <FaFileCsv size={20} /> CSV
                  </button>
                  <button
                    style={{
                      padding: "16px 20px",
                      borderRadius: 12,
                      background: "#2196f3",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      border: "none",
                      cursor: exportLoading ? "not-allowed" : "pointer",
                      opacity: exportLoading ? 0.6 : 1,
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                      boxShadow: "0 4px 16px rgba(33, 150, 243, 0.3)",
                      minWidth: "140px",
                    }}
                    disabled={exportLoading}
                    onClick={() => handleExport("excel")}
                  >
                    <FaFileExcel size={20} /> Excel
                  </button>
                  <button
                    style={{
                      padding: "16px 20px",
                      borderRadius: 12,
                      background: "#ff9800",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      border: "none",
                      cursor: exportLoading ? "not-allowed" : "pointer",
                      opacity: exportLoading ? 0.6 : 1,
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                      boxShadow: "0 4px 16px rgba(255, 152, 0, 0.3)",
                      minWidth: "140px",
                    }}
                    disabled={exportLoading}
                    onClick={() => handleExport("json")}
                  >
                    <FaFileCode size={20} /> JSON
                  </button>
                </div>
                {selectedTasks.length === 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      background: "#f8f9fa",
                      borderRadius: "8px",
                      border: "1px solid #e9ecef",
                      fontSize: "0.9rem",
                      color: "#666",
                    }}
                  >
                    💡 Tip: Use checkboxes to select specific tasks for export,
                    or export all filtered results
                  </div>
                )}
              </div>

              {exportLoading && (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 20,
                    color: "#666",
                    fontWeight: 500,
                  }}
                >
                  <div
                    className="spinner"
                    style={{
                      width: 24,
                      height: 24,
                      border: "3px solid #f3f3f3",
                      borderTop: "3px solid #7b6ef6",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin 0.7s linear infinite",
                      marginBottom: 10,
                    }}
                  ></div>
                  Preparing export...
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && (
        <div
          className="action-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.18)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setShowActionModal(false)}
        >
          <div
            className="action-modal"
            style={{
              background: "#fff",
              borderRadius: 18,
              boxShadow: "0 8px 32px rgba(60,60,120,0.18)",
              minWidth: 420,
              maxWidth: 540,
              width: "95%",
              padding: "32px 28px 28px 28px",
              position: "relative",
              animation: "fadeInModal 0.3s cubic-bezier(.4,2,.6,1)",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="action-modal-header"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                //marginBottom: 10,
              }}
            >
              <h3
                style={{
                  fontSize: "1.35rem",
                  fontWeight: 700,
                  color: "#22223b",
                  margin: 0,
                  letterSpacing: "-0.5px",
                }}
              >
                Change Task Status
              </h3>
              <button
                className="action-modal-close"
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 22,
                  color: "#888",
                  cursor: "pointer",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#f2f2f2")
                }
                onMouseOut={(e) => (e.currentTarget.style.background = "none")}
                onClick={handleCloseActionModal}
              >
                ×
              </button>
            </div>
            <div
              className="action-modal-content"
              style={{
                marginBottom: 10,
                color: "#444",
                fontSize: "1rem",
                fontWeight: 500,
              }}
            >
              <p
                style={{
                  margin: "0 0 18px 0",
                  color: "#6c6c80",
                  fontWeight: 500,
                }}
              >
                Select a new status for this task:
              </p>
              <div
                className="action-modal-buttons"
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <Select
                  value={selectedStatusOption}
                  onChange={setSelectedStatusOption}
                  options={[
                    { value: "Pending", label: "Pending" },
                    { value: "Planned", label: "Planned" },
                    { value: "WIP", label: "WIP" },
                    { value: "Completed", label: "Completed" },
                    { value: "Revalidate", label: "Revalidate" },
                    { value: "Delegated", label: "Delegate" },
                    { value: "Reopen", label: "Reopen" },
                  ]}
                  placeholder="Select status"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: 8,
                      borderColor: state.isFocused ? "#7b6ef6" : "#d3d3e7",
                      boxShadow: state.isFocused ? "0 0 0 2px #e0e7ff" : "none",
                      padding: "2px 2px",
                      fontWeight: 600,
                      fontSize: "1.08rem",
                      background: "#f8f8fa",
                      minHeight: 48,
                      cursor: "pointer",
                    }),
                    option: (base, state) => ({
                      ...base,
                      background: state.isSelected
                        ? "#7b6ef6"
                        : state.isFocused
                          ? "#ede9fe"
                          : "#fff",
                      color: state.isSelected ? "#fff" : "#22223b",
                      fontWeight: state.isSelected ? 700 : 500,
                      fontSize: "1.08rem",
                      cursor: "pointer",
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: 8,
                      boxShadow: "0 8px 32px rgba(60,60,120,0.18)",
                      zIndex: 9999,
                    }),
                  }}
                />
                {selectedStatusOption?.value && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {selectedStatusOption.value === "Delegated" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                          }}
                        >
                          <label
                            style={{
                              fontSize: "0.95rem",
                              fontWeight: 600,
                              color: "#444",
                              margin: 0,
                            }}
                          >
                            Tasks Delegation
                          </label>
                        </div>
                        {
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 10,
                              border: "1px solid #e0e7ff",
                              borderRadius: 12,
                              padding: "14px 16px",
                              background: "#f9f9ff",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                  color: "#6366f1",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                              </span>
                              <Select
                                value={delegationDraft.employee}
                                onChange={handleDelegationDraftEmployeeChange}
                                options={delegateOptions}
                                isLoading={delegateOptionsLoading}
                                isClearable
                                placeholder={
                                  delegateOptionsLoading
                                    ? "Loading employees..."
                                    : "Choose employee"
                                }
                                noOptionsMessage={() =>
                                  delegateOptionsLoading
                                    ? "Loading..."
                                    : "No employees found"
                                }
                                formatOptionLabel={formatDelegateOption}
                                filterOption={delegateFilterOption}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    borderRadius: 8,
                                    borderColor: state.isFocused
                                      ? "#7b6ef6"
                                      : "#d3d3e7",
                                    boxShadow: state.isFocused
                                      ? "0 0 0 2px #e0e7ff"
                                      : "none",
                                    minHeight: 40,
                                    background: "#fff",
                                    cursor: "pointer",
                                    fontSize: "0.88rem",
                                  }),
                                  placeholder: (base) => ({
                                    ...base,
                                    fontSize: "0.86rem",
                                  }),
                                }}
                              />
                              <input
                                type="text"
                                value={delegationDraft.taskName}
                                onChange={handleDelegationDraftFieldChange("taskName")}
                                placeholder="Task name"
                                style={{
                                  borderRadius: 8,
                                  border: "1px solid #d3d3e7",
                                  padding: "10px 12px",
                                  fontSize: "0.9rem",
                                  background: "#ffffff",
                                }}
                              />
                              <textarea
                                value={delegationDraft.description}
                                onChange={handleDelegationDraftFieldChange("description")}
                                placeholder="Task description"
                                rows={3}
                                style={{
                                  borderRadius: 8,
                                  border: "1px solid #d3d3e7",
                                  padding: "10px 12px",
                                  fontSize: "0.9rem",
                                  background: "#ffffff",
                                  resize: "vertical",
                                }}
                              />
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#6b7280",
                                }}
                              >
                                Click '<b>Add</b>' to queue another task.
                              </span>
                              <button
                                type="button"
                                onClick={handleAddDelegationEntry}
                                style={{
                                  border: "none",
                                  background: "#7b6ef6",
                                  color: "#fff",
                                  fontWeight: 600,
                                  padding: "8px 16px",
                                  borderRadius: 999,
                                  cursor: "pointer",
                                  fontSize: "0.86rem",
                                  boxShadow: "0 6px 20px rgba(123, 110, 246, 0.18)",
                                }}
                              >
                                Add
                              </button>
                            </div>
                            {delegationDraftError && (
                              <div
                                style={{
                                  color: "#dc2626",
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                }}
                              >
                                {delegationDraftError}
                              </div>
                            )}
                          </div>
                        }
                        {delegationEntries.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                              border: "1px solid #e4e4ef",
                              borderRadius: 10,
                              padding: "10px 12px",
                              background: "#f5f5fb",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "0.82rem",
                                fontWeight: 700,
                                color: "#4c51bf",
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                              }}
                            >
                              Delegation Queue
                            </div>
                            {delegationEntries.map((entry) => (
                              <div
                                key={entry.id}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 6,
                                  background: "#ffffff",
                                  borderRadius: 8,
                                  padding: "10px 12px",
                                  boxShadow: "0 4px 14px rgba(76, 81, 191, 0.08)",
                                  border: "1px solid #e0e7ff",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        fontSize: "0.92rem",
                                        color: "#1f2937",
                                      }}
                                    >
                                      {entry.employee?.label || "Unnamed"}
                                    </span>
                                    {entry.employee?.meta?.email ? (
                                      <span
                                        style={{
                                          fontSize: "0.78rem",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {entry.employee.meta.email}
                                      </span>
                                    ) : null}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveDelegationEntry(entry.id)}
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      color: "#dc2626",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.86rem",
                                    color: "#334155",
                                    fontWeight: 600,
                                  }}
                                >
                                  {entry.taskName}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.82rem",
                                    color: "#4b5563",
                                  }}
                                >
                                  {entry.description || "No description provided."}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div>
                      <textarea
                        value={statusRemark}
                        onChange={(e) => setStatusRemark(e.target.value)}
                        placeholder={`Add remark for ${selectedStatusOption.value} status`}
                        rows={3}
                        maxLength={2000}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: 8,
                          border: "1px solid #d3d3e7",
                          background: "#fafafa",
                          fontSize: "0.95rem",
                          outline: "none",
                        }}
                      />
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#666",
                          textAlign: "right",
                          marginTop: "4px",
                        }}
                      >
                        {statusRemark.length}/2000 characters
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <label
                        style={{
                          fontSize: "0.95rem",
                          fontWeight: 500,
                          color: "#444",
                        }}
                      >
                        Attach supporting files (optional)
                      </label>
                      <input
                        type="file"
                        multiple
                        onChange={handleStatusAttachmentInput}
                        disabled={updateLoading}
                        style={{
                          padding: "8px",
                          borderRadius: 8,
                          border: "1px dashed #b8b8d9",
                          background: "#f8f8fa",
                          fontSize: "0.92rem",
                          cursor: "pointer",
                        }}
                      />
                      {statusAttachments.length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {statusAttachments.map((file, index) => (
                            <div
                              key={`${file.name}-${file.lastModified || index}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                background: "#f3f4ff",
                                borderRadius: 8,
                                padding: "8px 12px",
                                fontSize: "0.9rem",
                                gap: 12,
                              }}
                            >
                              <span
                                style={{
                                  color: "#333",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                                title={file.name}
                              >
                                {file.name}
                                {file.size
                                  ? ` (${formatFileSize(file.size)})`
                                  : ""}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveStatusAttachment(index)}
                                disabled={updateLoading}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#d32f2f",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div
                        style={{
                          fontSize: "0.78rem",
                          color: "#888",
                        }}
                      >
                        Up to {MAX_STATUS_ATTACHMENTS} files, 15&nbsp;MB each.
                      </div>
                    </div>
                  </div>
                )}
                <button
                  style={{
                    marginTop: 12,
                    padding: "12px 0",
                    borderRadius: 8,
                    background: "#7b6ef6",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "1.08rem",
                    border: "none",
                    cursor:
                      selectedStatusOption &&
                        selectedStatusOption.value !== actionTask?.status &&
                        !updateLoading
                        ? "pointer"
                        : "not-allowed",
                    opacity:
                      selectedStatusOption &&
                        selectedStatusOption.value !== actionTask?.status &&
                        !updateLoading
                        ? 1
                        : 0.6,
                    transition: "background 0.2s, opacity 0.2s",
                    boxShadow: "0 2px 8px rgba(60,60,120,0.08)",
                    position: "relative",
                  }}
                  disabled={
                    !selectedStatusOption ||
                    selectedStatusOption.value === actionTask?.status ||
                    updateLoading
                  }
                  onClick={async () => {
                    if (
                      !selectedStatusOption ||
                      selectedStatusOption.value === actionTask?.status ||
                      updateLoading
                    )
                      return;
                    const newStatus = selectedStatusOption.value;
                    const trimmedRemark = statusRemark.trim();
                    const primaryDelegation =
                      delegationEntries.find((entry) => entry.employee?.value) || null;
                    if (newStatus === "Delegated" && !primaryDelegation) {
                      setUpdateError("Add at least one delegation task before updating.");
                      return;
                    }
                    setUpdateLoading(true);
                    setUpdateError("");

                    try {
                      const formData = new FormData();
                      formData.append("status", newStatus);
                      if (userId) {
                        formData.append("currentUserId", userId);
                      }
                      if (trimmedRemark) {
                        formData.append("statusRemark", trimmedRemark);
                      }
                      if (delegationEntries.length > 0) {
                        const queuedDelegations = delegationEntries
                          .filter((entry) => entry.employee?.value)
                          .map((entry) => ({
                            delegate_to: entry.employee.value,
                            taskName: entry.taskName,
                            description: entry.description,
                            delegateLabel: entry.employee.label,
                            delegateEmail:
                              entry.employee.meta?.email || null,
                          }));
                        if (queuedDelegations.length > 0) {
                          formData.append(
                            "delegationQueue",
                            JSON.stringify(queuedDelegations)
                          );
                        }
                      }
                      statusAttachments.forEach((file) => {
                        formData.append("statusAttachments", file);
                      });

                      const res = await fetch(
                        getApiUrl(`api/task/${actionTask.id}`),
                        {
                          method: "PUT",
                          body: formData,
                          credentials: "include",
                        }
                      );
                      const data = await res.json();
                      if (!res.ok) {
                        throw new Error(data.error || data.message || "Failed to update status");
                      }
                      toast.success(data.message || "Task status updated successfully!");

                      // Update UI only after successful response
                      setTasks(prev =>
                        prev.map((t) =>
                          t.id === actionTask.id ? { ...t, status: newStatus } : t
                        )
                      );

                      // Optimistic Status Count Update
                      setStatusCounts(prev => {
                        const newCounts = { ...prev };
                        const oldStatus = resolveStatusLabel(actionTask.status);
                        const nextStatus = resolveStatusLabel(newStatus);

                        if (oldStatus !== nextStatus) {
                          newCounts[oldStatus] = Math.max(0, (newCounts[oldStatus] || 0) - 1);
                          newCounts[nextStatus] = (newCounts[nextStatus] || 0) + 1;
                        }
                        return newCounts;
                      });

                      setShowActionModal(false);
                      setActionTask(null);

                      posthog.capture("Task Updated", {
                        taskStatus: newStatus,
                      });

                    } catch (err) {
                      toast.error(err.message || "Failed to update status. Please try again.");
                    } finally {
                      setUpdateLoading(false);
                      setStatusRemark("");
                      setStatusAttachments([]);
                      setDelegationEntries([]);
                      setDelegationDraft({
                        employee: null,
                        taskName: "",
                        description: "",
                      });
                      setShowDelegationComposer(false);
                      setDelegationDraftError("");
                    }
                  }}
                >
                  {updateLoading ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        className="spinner"
                        style={{
                          width: 18,
                          height: 18,
                          border: "2.5px solid #fff",
                          borderTop: "2.5px solid #7b6ef6",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: "spin 0.7s linear infinite",
                        }}
                      ></span>
                      Updating...
                    </span>
                  ) : (
                    "Update"
                  )}
                </button>
                {updateError && (
                  <div style={{ color: "red", marginTop: 8, fontWeight: 500 }}>
                    {updateError}
                  </div>
                )}
                <div
                  style={{
                    marginTop: 12,
                    padding: "10px 12px",
                    background: "#f8f8fa",
                    border: "1px solid #e0e0ef",
                    borderRadius: 8,
                    fontSize: "0.85rem",
                    color: "#555",
                    lineHeight: 1.4,
                  }}
                >
                  Editing and deletion of tasks is managed by the task owner.
                  You can still update the status or add remarks here.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="taskboard-header">
        <h2>My Tasks</h2>
        <div className="taskboard-header-actions">
          <button
            className="taskboard-btn"
            onClick={() => setShowAddTask(true)}
          >
            Create New Task
          </button>
          <button
            className="taskboard-btn"
            onClick={handleBulkComplete}
            disabled={selectedTasks.length === 0 || bulkCompleteLoading}
            style={{
              background: "#dc3545",
              opacity:
                selectedTasks.length === 0 || bulkCompleteLoading ? 0.6 : 1,
              cursor:
                selectedTasks.length === 0 || bulkCompleteLoading
                  ? "not-allowed"
                  : "pointer",
              transition: "all 0.2s",
            }}
            onMouseOver={(e) => {
              if (selectedTasks.length > 0 && !bulkCompleteLoading) {
                e.target.style.background = "#c82333";
                e.target.style.transform = "translateY(-1px)";
              }
            }}
            onMouseOut={(e) => {
              if (selectedTasks.length > 0 && !bulkCompleteLoading) {
                e.target.style.background = "#dc3545";
                e.target.style.transform = "translateY(0)";
              }
            }}
          >
            {bulkCompleteLoading ? (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  className="spinner"
                  style={{
                    width: 16,
                    height: 16,
                    border: "2px solid #fff",
                    borderTop: "2px solid #7b6ef6",
                    borderRadius: "50%",
                    display: "inline-block",
                    animation: "spin 0.7s linear infinite",
                  }}
                ></span>
                Completing...
              </span>
            ) : (
              `Bulk Complete ${selectedTasks.length > 0 ? `(${selectedTasks.length})` : ""
              }`
            )}
          </button>
          <button
            className="taskboard-btn"
            onClick={() => setShowExportModal(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              transition: "all 0.2s",
              background: "#dc3545",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#c82333";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "#dc3545";
              e.target.style.transform = "translateY(0)";
            }}
          >
            <FaDownload size={14} />
            Export Data
          </button>
          <div
            className="taskboard-search-wrapper"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <input
              className="taskboard-search"
              type="text"
              placeholder="Search tasks, names, emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                minWidth: "200px",
                padding: "8px 12px",
                paddingRight: searchQuery ? "40px" : "12px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "0.9rem",
                outline: "none",
                transition: "all 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#7b6ef6";
                e.target.style.boxShadow = "0 0 0 2px rgba(123, 110, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#ddd";
                e.target.style.boxShadow = "none";
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={{
                  position: "absolute",
                  right: "8px",
                  background: "none",
                  border: "none",
                  color: "#999",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "16px",
                  transition: "all 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "#f0f0f0";
                  e.target.style.color = "#666";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "none";
                  e.target.style.color = "#999";
                }}
                title="Clear search"
              >
                ×
              </button>
            )}

            <button
              className="taskboard-btn"
              onClick={() => {
                setAppliedSearch(searchQuery);
                setCurrentPage(1);
              }}
              style={{
                marginLeft: "8px",
                padding: "8px 16px",
                fontSize: "0.85rem",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(123, 110, 246, 0.2)",
                transition: "all 0.2s"
              }}
              onMouseOver={(e) => {
                e.target.style.transform = "translateY(-1px)";
              }}
              onMouseOut={(e) => {
                e.target.style.transform = "translateY(0)";
              }}
            >
              <FaFilter style={{ marginRight: "6px" }} /> Apply
            </button>

            <button
              className="filter-btn"
              onClick={() => {
                setSearchQuery("");
                setAppliedSearch("");
                setSelectedStatus("All");
                setCurrentPage(1);
              }}
              style={{
                marginLeft: "8px",
                padding: "8px 16px",
                fontSize: "0.85rem",
                background: "#fff",
                color: "#666",
                border: "1px solid #ddd",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.2s"
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#f5f5f5";
                e.target.style.color = "#333";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "#fff";
                e.target.style.color = "#666";
              }}
            >
              Reset
            </button>

            {/* Tooltip for manual search */}
            {searchQuery && searchQuery !== appliedSearch && (
              <div style={{
                position: "absolute",
                top: "100%",
                right: "0",
                marginTop: "12px",
                background: "#333",
                color: "#fff",
                padding: "8px 12px",
                borderRadius: "6px",
                fontSize: "0.75rem",
                zIndex: 100,
                pointerEvents: "none",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                animation: "fadeIn 0.2s ease-in-out"
              }}>
                Click <b>Apply Filter</b> to search
                <div style={{
                  position: "absolute",
                  top: "-4px",
                  right: "40px",
                  width: "8px",
                  height: "8px",
                  background: "#333",
                  transform: "rotate(45deg)"
                }}></div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="status-cards-row">
        {statusCards.map((card) => (
          <div
            key={card.label}
            className={`status-card${selectedStatus === card.label ? " selected" : ""
              }`}
            style={{
              background: card.color,
              cursor: "pointer",
              ...(selectedStatus === card.label
                ? { "--status-glow": card.color }
                : {}),
            }}
            onClick={() => { setCurrentPage(1); setSelectedStatus(card.label); }}
          >
            <div className="status-label">{card.label}</div>
            <div className="status-count">
              {statusCounts[card.label] || 0}
            </div>
          </div>
        ))}
      </div>

      {selectedTasks.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 18px",
            background: "linear-gradient(0deg, #eef9f0, #eef9f0)",
            border: "1px solid #b7e1c1",
            borderRadius: "12px",
            marginTop: "8px",
            marginBottom: "12px",
            boxShadow: "0 6px 20px rgba(46, 125, 50, 0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              color: "#2e7d32",
              fontWeight: 700,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#2e7d32",
                color: "#fff",
                fontSize: 14,
                boxShadow: "0 2px 8px rgba(46,125,50,0.25)",
              }}
            >
              ✓
            </span>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2px" }}
            >
              <span style={{ fontSize: "0.98rem" }}>
                {selectedTasks.length} task
                {selectedTasks.length !== 1 ? "s" : ""} selected
              </span>
              {(() => {
                const tasksToComplete = filteredTasks.filter((task) =>
                  selectedTasks.includes(task.id)
                );
                const incompleteTasks = tasksToComplete.filter(
                  (task) => task.status !== "Completed"
                );
                if (incompleteTasks.length !== selectedTasks.length) {
                  return (
                    <span
                      style={{
                        fontSize: "0.8rem",
                        color: "#1b5e20",
                        fontWeight: 500,
                      }}
                    >
                      {incompleteTasks.length} will be completed (
                      {selectedTasks.length - incompleteTasks.length} already
                      completed)
                    </span>
                  );
                }
                return null;
              })()}
            </div>
          </div>
          <button
            onClick={() => {
              setSelectedTasks([]);
              setSelectAll(false);
            }}
            style={{
              background: "transparent",
              border: "1.5px solid #2e7d32",
              color: "#2e7d32",
              padding: "8px 14px",
              borderRadius: "999px",
              cursor: "pointer",
              fontSize: "0.85rem",
              fontWeight: 700,
              transition: "all 0.2s ease",
              letterSpacing: 0.2,
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#2e7d32";
              e.target.style.color = "#fff";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "transparent";
              e.target.style.color = "#2e7d32";
              e.target.style.transform = "translateY(0)";
            }}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Table Section with Loading Overlay */}
      <div style={{ position: 'relative', minHeight: '400px' }}>
        {/* Loading Overlay */}
        {isTaskReloading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: '12px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f4f6',
              borderTop: '4px solid #e30613',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}></div>
            <p style={{
              marginTop: '16px',
              color: '#64748b',
              fontSize: '0.95rem',
              fontWeight: 500
            }}>
              Loading tasks...
            </p>
            <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
          </div>
        )}

        {/* Table Content */}
        <div style={{ opacity: isTaskReloading ? 0.4 : 1, transition: 'opacity 0.2s' }}>
          <table className="taskboard-table">
            <thead>
              <tr>
                <th style={{ width: "50px", textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      accentColor: "#7b6ef6",
                    }}
                  />
                </th>
                <th>S. No.</th>
                <th>Task Name</th>
                <th>Target End Date</th>
                <th>Over Due</th>
                <th>Status</th>
                <th>Given By</th>
                <th>View</th>
                <th>Edit</th>
              </tr>
            </thead>
            <tbody>
              {isTaskReloading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center", padding: "24px" }}>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
                      <div className="table-loader" />
                      <span>Loading Tasks...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredTasks.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center" }}>
                    No tasks for this status
                  </td>
                </tr>
              ) : (
                filteredTasks.map((task, idx) => {
                  const rowId = task.id ?? idx;
                  const isHighlighted =
                    highlightTaskId && String(rowId) === String(highlightTaskId);
                  const { label: displayStatus, normalized } =
                    resolveAggregatedDisplayStatus(task, resolveStatusLabel);
                  const statusBadgeKey = badgeClassLookup[normalized] || "Pending";
                  const statusTooltip = formatStatusBreakdownTooltip(
                    task,
                    resolveStatusLabel
                  );

                  return (
                    <tr
                      key={rowId}
                      data-task-row={rowId}
                      className={`taskboard-row${isHighlighted ? " taskboard-row--highlight" : ""
                        }`}
                    >
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id)}
                          onChange={() => handleTaskSelect(task.id)}
                          style={{
                            width: "16px",
                            height: "16px",
                            cursor: "pointer",
                            accentColor: "#7b6ef6",
                          }}
                        />
                      </td>
                      <td>{idx + 1}</td>
                      <td>
                        <b>{(task.title || task.name || "").toUpperCase()}</b>
                      </td>
                      <td>{formatDate(task.due_date)}</td>
                      <td>{resolveOverdueDisplayString(task)}</td>
                      <td>
                        <span
                          className={`taskboard-status-badge ${statusBadgeKey}`}
                          title={statusTooltip || undefined}
                        >
                          {displayStatus}
                        </span>
                      </td>
                      <td>
                        <div>
                          {`${task.GivenByFirstName || ""} ${task.GivenByLastName || ""
                            }`.trim() ||
                            `${task.FirstName || ""} ${task.LastName || ""
                              }`.trim() ||
                            task.givenBy ||
                            ""}
                        </div>
                        <div className="taskboard-email">
                          {task.GivenByEmail ||
                            task.email ||
                            task.givenByEmail ||
                            ""}
                        </div>
                      </td>
                      <td>
                        <button
                          className="taskboard-view-btn"
                          onClick={() => {
                            setSelectedTask(task);
                            setShowDetails(true);
                          }}
                        >
                          <FaEye />
                        </button>
                      </td>
                      <td>
                        <button
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = "#f5f5f5";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "#fff";
                          }}
                          onClick={() => handleActionClick(task)}
                        >
                          <FaEdit size={16} color="#555" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalTasks}
        pageSize={pageLimit}
        onPageChange={(page) => setCurrentPage(page)}
        onPageSizeChange={(size) => {
          setPageLimit(size);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default TaskBoard;
